import * as React from 'react';
import type { ReactNode } from 'react';
import { useLocalStorage } from 'react-use';

import { USER_PREFERENCES_KEY } from 'src/const/app';

const initialPreferences = {
  showActiveJobsOnly: false,
};

interface Props {
  children: ReactNode;
}

interface Preferences {
  showActiveJobsOnly: boolean;
}

interface PreferenceContextValue {
  preferences: Preferences;
  setPreferences: React.Dispatch<React.SetStateAction<Preferences | undefined>>;
}

const PreferenceContext = React.createContext<PreferenceContextValue | null>(
  null,
);

PreferenceContext.displayName = 'PreferenceContext';

export const PreferencesProvider = ({ children }: Props) => {
  const [preferences, setPreferences] = useLocalStorage(
    USER_PREFERENCES_KEY,
    initialPreferences,
  );

  return (
    <PreferenceContext.Provider
      value={{
        preferences: preferences ?? initialPreferences,
        setPreferences,
      }}
    >
      {children}
    </PreferenceContext.Provider>
  );
};

export const usePreferences: () => PreferenceContextValue = () => {
  const context = React.useContext(PreferenceContext);
  if (!context) {
    throw new Error(
      'usePreferenceContext must be used within a PreferenceContextProvider',
    );
  }

  return context;
};
